import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ArrowLeft, Calculator, TrendingUp, Info, AlertTriangle } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';

const STORAGE_KEY = 'mutual_fund_data';

export const MutualFundCalculator = () => {
  const [investmentType, setInvestmentType] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).investmentType : 'lumpsum';
  });
  
  const [investmentAmount, setInvestmentAmount] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).investmentAmount : 100000;
  });
  
  const [expectedReturn, setExpectedReturn] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).expectedReturn : 12;
  });
  
  const [timeHorizon, setTimeHorizon] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).timeHorizon : 10;
  });
  
  const [expenseRatio, setExpenseRatio] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).expenseRatio : 1.5;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      investmentType,
      investmentAmount,
      expectedReturn,
      timeHorizon,
      expenseRatio
    }));
  }, [investmentType, investmentAmount, expectedReturn, timeHorizon, expenseRatio]);

  const calculateReturns = () => {
    const amount = parseFloat(investmentAmount) || 0;
    const returnRate = (parseFloat(expectedReturn) || 0) / 100;
    const years = parseFloat(timeHorizon) || 0;
    const expense = (parseFloat(expenseRatio) || 0) / 100;
    const netReturn = returnRate - expense;

    let finalCorpus = 0;
    let totalInvested = 0;
    let finalCorpusWithoutExpense = 0;

    if (investmentType === 'lumpsum') {
      totalInvested = amount;
      finalCorpus = amount * Math.pow(1 + netReturn, years);
      finalCorpusWithoutExpense = amount * Math.pow(1 + returnRate, years);
    } else {
      // SIP calculation
      totalInvested = amount * 12 * years;
      const monthlyReturn = netReturn / 12;
      const monthlyReturnWithoutExpense = returnRate / 12;
      const months = years * 12;
      
      finalCorpus = amount * ((Math.pow(1 + monthlyReturn, months) - 1) / monthlyReturn) * (1 + monthlyReturn);
      finalCorpusWithoutExpense = amount * ((Math.pow(1 + monthlyReturnWithoutExpense, months) - 1) / monthlyReturnWithoutExpense) * (1 + monthlyReturnWithoutExpense);
    }

    const netGains = finalCorpus - totalInvested;
    const expenseImpact = finalCorpusWithoutExpense - finalCorpus;
    const cagr = years > 0 ? (Math.pow(finalCorpus / totalInvested, 1 / years) - 1) * 100 : 0;

    return {
      finalCorpus,
      totalInvested,
      netGains,
      cagr,
      expenseImpact,
      netReturn: netReturn * 100
    };
  };

  const generateGrowthData = () => {
    const amount = parseFloat(investmentAmount) || 0;
    const returnRate = (parseFloat(expectedReturn) || 0) / 100;
    const years = parseFloat(timeHorizon) || 0;
    const expense = (parseFloat(expenseRatio) || 0) / 100;
    const netReturn = returnRate - expense;
    
    const data = [];
    
    for (let year = 0; year <= years; year++) {
      let invested = 0;
      let valueWithExpense = 0;
      let valueWithoutExpense = 0;

      if (investmentType === 'lumpsum') {
        invested = amount;
        valueWithExpense = amount * Math.pow(1 + netReturn, year);
        valueWithoutExpense = amount * Math.pow(1 + returnRate, year);
      } else {
        invested = amount * 12 * year;
        const monthlyReturn = netReturn / 12;
        const monthlyReturnWithoutExpense = returnRate / 12;
        const months = year * 12;
        
        if (months > 0) {
          valueWithExpense = amount * ((Math.pow(1 + monthlyReturn, months) - 1) / monthlyReturn) * (1 + monthlyReturn);
          valueWithoutExpense = amount * ((Math.pow(1 + monthlyReturnWithoutExpense, months) - 1) / monthlyReturnWithoutExpense) * (1 + monthlyReturnWithoutExpense);
        }
      }

      data.push({
        year: `Year ${year}`,
        invested: Math.round(invested),
        withExpense: Math.round(valueWithExpense),
        withoutExpense: Math.round(valueWithoutExpense)
      });
    }

    return data;
  };

  const generateExpenseImpactData = () => {
    const amount = parseFloat(investmentAmount) || 0;
    const returnRate = (parseFloat(expectedReturn) || 0) / 100;
    const years = parseFloat(timeHorizon) || 0;
    
    const expenseRatios = [0.5, 1.0, 1.5, 2.0, 2.5];
    
    return expenseRatios.map(expense => {
      const netReturn = returnRate - (expense / 100);
      let finalCorpus = 0;

      if (investmentType === 'lumpsum') {
        finalCorpus = amount * Math.pow(1 + netReturn, years);
      } else {
        const monthlyReturn = netReturn / 12;
        const months = years * 12;
        finalCorpus = amount * ((Math.pow(1 + monthlyReturn, months) - 1) / monthlyReturn) * (1 + monthlyReturn);
      }

      return {
        expenseRatio: `${expense}%`,
        corpus: Math.round(finalCorpus)
      };
    });
  };

  const results = calculateReturns();
  const growthData = generateGrowthData();
  const expenseImpactData = generateExpenseImpactData();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  Mutual Fund Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Explanation
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is a Mutual Fund?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  A mutual fund pools money from multiple investors to invest in a diversified portfolio of stocks, bonds, or other securities. 
                  Professional fund managers manage these investments, aiming to generate returns while spreading risk across various assets.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Input Parameters</CardTitle>
                  <CardDescription>Enter your investment details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Investment Type</Label>
                    <RadioGroup value={investmentType} onValueChange={setInvestmentType}>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="lumpsum" id="lumpsum" />
                        <Label htmlFor="lumpsum" className="font-normal cursor-pointer">Lump Sum</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="sip" id="sip" />
                        <Label htmlFor="sip" className="font-normal cursor-pointer">SIP (Monthly)</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="investmentAmount" className="text-sm font-medium">
                      {investmentType === 'lumpsum' ? 'Investment Amount ($)' : 'Monthly SIP Amount ($)'}
                    </Label>
                    <Input
                      id="investmentAmount"
                      type="number"
                      value={investmentAmount}
                      onChange={(e) => setInvestmentAmount(e.target.value)}
                      placeholder="100000"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="expectedReturn" className="text-sm font-medium">
                      Expected Annual Return (%)
                    </Label>
                    <Input
                      id="expectedReturn"
                      type="number"
                      value={expectedReturn}
                      onChange={(e) => setExpectedReturn(e.target.value)}
                      placeholder="12"
                      step="0.1"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Typical equity: 10-15%, Debt: 6-8%</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="timeHorizon" className="text-sm font-medium">
                      Time Horizon (Years)
                    </Label>
                    <Input
                      id="timeHorizon"
                      type="number"
                      value={timeHorizon}
                      onChange={(e) => setTimeHorizon(e.target.value)}
                      placeholder="10"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="expenseRatio" className="text-sm font-medium">
                      Expense Ratio (%)
                    </Label>
                    <Input
                      id="expenseRatio"
                      type="number"
                      value={expenseRatio}
                      onChange={(e) => setExpenseRatio(e.target.value)}
                      placeholder="1.5"
                      step="0.1"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Annual fund management fee</p>
                  </div>

                  <Button 
                    className="w-full gradient-primary hover:opacity-90 transition-opacity shadow-secondary"
                    onClick={() => setInvestmentAmount(investmentAmount)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate
                  </Button>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Investment Returns</CardTitle>
                    <CardDescription>Your mutual fund growth projection</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Final Corpus</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-primary">
                          {formatCurrency(results.finalCorpus)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">after {timeHorizon} years</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Total Invested</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-secondary">
                          {formatCurrency(results.totalInvested)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Net Gains</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-accent">
                          {formatCurrency(results.netGains)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">CAGR: {results.cagr.toFixed(2)}%</p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-destructive/10 to-card border border-destructive/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-destructive flex items-center justify-center">
                            <AlertTriangle className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Expense Impact</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-destructive">
                          {formatCurrency(results.expenseImpact)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">Lost to fees</p>
                      </div>
                    </div>

                    <Separator className="my-6" />

                    <div className="p-6 rounded-xl bg-gradient-chart">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">Net Annual Return</p>
                          <p className="text-2xl font-display font-bold text-foreground mb-1">
                            {results.netReturn.toFixed(2)}%
                          </p>
                          <p className="text-sm text-muted-foreground">
                            After {expenseRatio}% expense ratio
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">Investment Mode</p>
                          <p className="text-2xl font-display font-bold text-foreground mb-1">
                            {investmentType === 'lumpsum' ? 'Lump Sum' : 'SIP'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {investmentType === 'sip' ? `${timeHorizon * 12} monthly contributions` : 'One-time investment'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">NAV-Based Growth Simulation</CardTitle>
                <CardDescription className="text-base">
                  Track your investment growth over time with and without expense ratio
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={growthData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="year" 
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="invested" 
                        stackId="1"
                        stroke="hsl(var(--muted-foreground))" 
                        fill="hsl(var(--muted))" 
                        name="Total Invested"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="withExpense" 
                        stackId="2"
                        stroke="hsl(var(--secondary))" 
                        fill="hsl(var(--secondary))" 
                        fillOpacity={0.6}
                        name="Value (After Expense)"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="withoutExpense" 
                        stackId="3"
                        stroke="hsl(var(--primary))" 
                        fill="hsl(var(--primary))" 
                        fillOpacity={0.3}
                        name="Value (Before Expense)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Expense Ratio Impact Over Time</CardTitle>
                  <CardDescription>
                    How different expense ratios affect your final corpus
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={expenseImpactData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="expenseRatio" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 100000).toFixed(0)}L`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Bar dataKey="corpus" fill="hsl(var(--accent))" name="Final Corpus" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Investment vs Returns</CardTitle>
                  <CardDescription>
                    Wealth creation breakdown
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={[
                        { name: 'Invested', value: results.totalInvested, fill: 'hsl(var(--muted))' },
                        { name: 'Returns', value: results.netGains, fill: 'hsl(var(--secondary))' },
                        { name: 'Expense Loss', value: results.expenseImpact, fill: 'hsl(var(--destructive))' }
                      ]}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="name" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 100000).toFixed(0)}L`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Bar dataKey="value" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding Mutual Funds</CardTitle>
                <CardDescription className="text-base">
                  Learn how mutual funds grow your money and key factors to consider
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    How Mutual Funds Grow Your Money
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    Your investment of <strong className="text-primary">{formatCurrency(results.totalInvested)}</strong> can grow to 
                    <strong className="text-primary"> {formatCurrency(results.finalCorpus)}</strong> over {timeHorizon} years, generating 
                    <strong className="text-primary"> {formatCurrency(results.netGains)}</strong> in returns.
                  </p>
                  <div className="p-4 rounded-lg bg-muted/50 text-sm space-y-2">
                    <p className="font-semibold">Growth Calculation:</p>
                    <p className="font-mono">
                      {investmentType === 'lumpsum' 
                        ? `Final Value = Principal × (1 + Return Rate)^Years`
                        : `Final Value = Monthly SIP × [((1 + r)^n - 1) / r] × (1 + r)`
                      }
                    </p>
                    <p className="font-mono">
                      Where: Return Rate = {expectedReturn}% - {expenseRatio}% = {results.netReturn.toFixed(2)}%
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    Expense Ratio Erosion Effect
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    The {expenseRatio}% annual expense ratio will cost you <strong className="text-destructive">{formatCurrency(results.expenseImpact)}</strong> over 
                    {timeHorizon} years. This is money that goes to fund management fees instead of growing your wealth.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-primary-light border border-primary/20">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Without Expense</p>
                      <p className="text-2xl font-bold text-primary mb-2">
                        {formatCurrency(results.finalCorpus + results.expenseImpact)}
                      </p>
                      <p className="text-sm text-muted-foreground">Theoretical maximum</p>
                    </div>
                    <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                      <p className="text-sm font-medium text-muted-foreground mb-1">After Expense</p>
                      <p className="text-2xl font-bold text-destructive mb-2">
                        {formatCurrency(results.finalCorpus)}
                      </p>
                      <p className="text-sm text-muted-foreground">Actual corpus</p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent">
                    Market Risk vs Diversification
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-accent font-semibold flex-shrink-0">📊</span>
                      <div>
                        <strong className="text-foreground">Market Volatility:</strong> The {expectedReturn}% expected return is 
                        an average. Actual returns will fluctuate year-to-year based on market conditions.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-accent font-semibold flex-shrink-0">🎯</span>
                      <div>
                        <strong className="text-foreground">Diversification Benefits:</strong> Mutual funds spread your money across 
                        multiple securities, reducing the impact of any single investment's poor performance.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-accent font-semibold flex-shrink-0">⚡</span>
                      <div>
                        <strong className="text-foreground">Risk-Return Trade-off:</strong> Higher expected returns typically come with 
                        higher risk. Your {expectedReturn}% target suggests a {expectedReturn >= 12 ? 'high-equity' : expectedReturn >= 8 ? 'balanced' : 'conservative'} portfolio.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple">
                    Fund Type Suggestions
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">🚀 Equity Funds (10-15% returns)</h4>
                      <p className="text-muted-foreground">
                        Best for long-term wealth creation (7+ years). Higher risk but potentially higher returns. 
                        Suitable if your target return is 12% or above.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">🔄 Hybrid Funds (8-12% returns)</h4>
                      <p className="text-muted-foreground">
                        Balanced mix of equity and debt. Moderate risk with stable returns. 
                        Good for medium-term goals (3-7 years).
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">🛡️ Debt Funds (6-8% returns)</h4>
                      <p className="text-muted-foreground">
                        Lower risk, stable returns. Best for short-term goals (1-3 years) or conservative investors. 
                        Consider if you're targeting 8% or below.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    Important Risks and Considerations
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Past Performance:</strong> Historical returns don't guarantee 
                        future results. Markets can be unpredictable, especially in the short term.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Investment Horizon:</strong> {timeHorizon < 3 ? 'Your short time horizon increases risk. Consider debt funds.' : 
                        timeHorizon < 7 ? 'Your medium-term horizon is suitable for balanced/hybrid funds.' : 
                        'Your long-term horizon is ideal for equity funds to ride out market volatility.'}
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Exit Loads & Lock-ins:</strong> Some funds charge fees for early 
                        withdrawal. Check the terms before investing.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Tax Implications:</strong> Long-term capital gains above $1.25 lakh 
                        are taxed at 12.5%. Short-term gains are taxed at 20%. Plan accordingly.
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default MutualFundCalculator;